import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { tooltipStyle } from "./oversiktConstants";
import { dec, num } from "@/lib/formatters";

const fmtPct = (v) => (v == null ? "—" : `${dec(v, 1)} %`);
const fmtDiff = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${dec(v, 1)}`);
const diffClass = (v) => {
  if (v > 0) return "text-[#3F8A86]";
  if (v < 0) return "text-[#D35E4E]";
  return "text-[#4B647F]";
};

// rows: [{ key, name, color, share, fylkeShare, landShare }]
export const ShareTable = ({ rows, firstColumn, hasFylke, hasLand, testId, fylkeLabel = "Fylke" }) => (
  <table className="w-full mt-2 text-sm" data-testid={`${testId}-legend`}>
    <thead>
      <tr className="text-xs text-[#8AA4BF]">
        <th className="text-left font-medium pb-1">{firstColumn}</th>
        <th className="text-right font-medium pb-1">Kommune</th>
        {hasFylke && <th className="text-right font-medium pb-1" data-testid={`${testId}-fylke-header`}>{fylkeLabel}</th>}
        {hasLand && <th className="text-right font-medium pb-1" data-testid={`${testId}-land-header`}>Landet</th>}
        {hasFylke && <th className="text-right font-medium pb-1">Avvik {fylkeLabel.toLowerCase()}</th>}
      </tr>
    </thead>
    <tbody>
      {rows.map((d) => (
        <tr key={d.key} data-testid={`${testId}-row-${d.key}`}>
          <td className="py-0.5">
            <span className="flex items-center gap-2 text-[#1F2A36]">
              <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.color }} />
              {d.name}
            </span>
          </td>
          <td className="text-right text-[#1F2A36] tabular-nums font-medium">{fmtPct(d.share)}</td>
          {hasFylke && <td className="text-right text-[#4B647F] tabular-nums">{fmtPct(d.fylkeShare)}</td>}
          {hasLand && <td className="text-right text-[#4B647F] tabular-nums">{fmtPct(d.landShare)}</td>}
          {hasFylke && (
            <td className={`text-right tabular-nums ${diffClass(d.fylkeShare != null ? d.share - d.fylkeShare : null)}`}>
              {fmtDiff(d.fylkeShare != null ? d.share - d.fylkeShare : null)}
            </td>
          )}
        </tr>
      ))}
    </tbody>
  </table>
);

// Grouped horizontal bars: Kommune vs fylke vs land (share %)
export const ShareCompareChart = ({ rows, fylkeName, landName, year, subject, testId, height = 340, snittOrd = "fylkessnittet" }) => {
  const data = rows.map((d) => ({
    name: d.name,
    Kommune: +(d.share ?? 0).toFixed(1),
    ...(fylkeName ? { [fylkeName]: d.fylkeShare ?? 0 } : {}),
    ...(landName ? { [landName]: d.landShare ?? 0 } : {}),
  }));
  return (
    <div data-testid={`${testId}-fylke-compare`}>
      <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-3">
        Kommunen mot {fylkeName ? snittOrd : ""}{fylkeName && landName ? " og " : ""}{landName ? "landet" : ""} {year} ({subject}, %)
      </p>
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" barGap={1} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
            <XAxis type="number" stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => `${num(v)} %`} />
            <YAxis type="category" dataKey="name" stroke="#8AA4BF" fontSize={12} width={230} interval={0} />
            <Tooltip formatter={(v) => `${num(v)} %`} contentStyle={tooltipStyle} />
            <Legend />
            <Bar dataKey="Kommune" fill="#103766" radius={[0, 4, 4, 0]} />
            {fylkeName && <Bar dataKey={fylkeName} fill="#8AA4BF" radius={[0, 4, 4, 0]} />}
            {landName && <Bar dataKey={landName} fill="#E8A34A" radius={[0, 4, 4, 0]} />}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
